<template>
	<Page class="christmas-offer-paywall">
		<template v-slot:content>
			<SafeAreaTop
				class="christmas-offer-paywall__safe-area"
				closePosition="left"
				@close="onClose"
				theme="dark-invert"
			/>

			<div class="christmas-offer-paywall__header">
				<img
					class="christmas-offer-paywall__header__img"
					src="@/assets/christmas-offer-paywall-bg.png"
					alt="Image with pines"
				/>
				<ChristmasOfferTitle class="christmas-offer-paywall__header__title" />
			</div>

			<div class="christmas-offer-paywall__content container">
				<h3 class="christmas-offer-paywall__subtitle">
					{{ t('offers.christmas.paywall.list.title') }}
				</h3>
				<List
					class="christmas-offer-paywall__list"
					:items="[
						t('offers.christmas.paywall.list.customized'),
						t('offers.christmas.paywall.list.easy_to_follow'),
						t('offers.christmas.paywall.list.regular_diet'),
						t('offers.christmas.paywall.list.adapted'),
						t('offers.christmas.paywall.list.expert_support')
					]"
				/>
				<PaywallSwitcher
					class="christmas-offer-paywall__switcher"
					:label="t('offers.christmas.paywall.switcher_label')"
					theme="yellow"
					v-model="applyDiscount"
				/>
				<ProductRadioSelect
					class="christmas-offer-paywall__products"
					theme="yellow"
					:products="products"
					:active="activeProduct"
					@select="handleSelect"
				/>
				<div v-if="activeProduct.terms" class="christmas-offer-paywall__terms">{{ activeProduct.terms }}</div>
				<PaywallLinksBlock class="christmas-offer-paywall__links" theme="light" @restore="handleRestore" />
			</div>
		</template>

		<template v-slot:footer>
			<div class="container">
				<Button @click="handleSubmit" class="btn christmas-offer-paywall__btn">{{
					t('offers.christmas.paywall.continue_btn')
				}}</Button>
			</div>
		</template>
	</Page>
</template>

<script>
import { inject, computed, ref, onMounted, watch } from 'vue'
import useApi from '@/composition/api.js'

import Page from '@/components/Page'
import SafeAreaTop from '@/components/SafeAreaTop'
import Button from '@/components/Button.vue'
import BlackFridayLogo from '@/components/black-friday/Logo.vue'
import BlackFridayAdvantages from '@/components/black-friday/Advantages.vue'
import ProductRadioSelect from '@/components/ProductRadioSelect'
import { useStore } from 'vuex'
import PaywallSwitcher from '@/components/Switcher.vue'
import PaywallLinksBlock from '../../components/PaywallLinksBlock.vue'
import List from '../../components/List.vue'
import { useI18n } from 'vue-i18n'
import ChristmasOfferTitle from '../../components/christmas-offer/ChristmasOfferTitle.vue'

export default {
	components: {
		Page,
		SafeAreaTop,
		Button,
		BlackFridayLogo,
		BlackFridayAdvantages,
		ProductRadioSelect,
		PaywallSwitcher,
		PaywallLinksBlock,
		List,
		ChristmasOfferTitle
	},

	setup() {
		const amplitude = inject('amplitude')
		const api = useApi()
		const store = useStore()
		const { t } = useI18n()

		const applyDiscount = ref(true)
		watch(
			() => applyDiscount.value,
			(val) => {
				activeProduct.value = products.value[0]
				amplitude.logEvent('PaywallToggleButtonClick', {
					action: val ? 'turn_on' : 'turn_off',
					offer: 'Christmas2022'
				})
			}
		)

		onMounted(() => {
			amplitude.logEvent(store.getters['getAmplitudeEventName']('PaywallShow'), {
				product_ids: products.value.map((p) => p?.id),
				prices: products.value.map((p) => p?.product_price),
				currency: products.value[0]?.currency?.toLowerCase(),
				data_source: 'client',
				offer: 'Christmas2022'
			})
		})

		const products = computed(() =>
			store.getters['products/getData']([
				applyDiscount.value
					? {
							id: 'simple_xmas_offer_subscription_3_month_discount',
							text: `${t('product.quarterly')}: [introductory_price]`,
							badge: t('product.badge_discount_percent_off', { count: '50' }),
							terms: t('product.quarterly_discount_terms', { count: '50' })
					  }
					: {
							id: 'simple_xmas_offer_subscription_3_month_wo_discount',
							text: `${t('product.quarterly')}: [price]`,
							terms: t('product.quarterly_terms')
					  },
				{
					id: 'simple_xmas_offer_subscription_annual',
					text: `${t('product.annual')}: [price]`,
					terms: t('product.annyally_terms')
				}
			])
		)
		const activeProduct = ref(products.value[0])

		const onClose = () => {
			amplitude.logEvent(store.getters['getAmplitudeEventName']('PaywallDone'), {
				action: 'close',
				offer: 'Christmas2022'
			})
			api.close()
		}

		const handleSelect = (product) => (activeProduct.value = product)

		const handleRestore = () =>
			api.restore({
				closeOnSuccess: true
			})

		const handleSubmit = () =>
			api.purchase({
				productId: activeProduct.value.id,
				onFinish: () => {
					amplitude.logEvent(store.getters['getAmplitudeEventName']('PaywallDone'), {
						action: 'Subscribe',
						product_id: activeProduct.value.id,
						duration_month: activeProduct.value.duration_months,
						price: Math.abs(activeProduct.value.product_price),
						currency: activeProduct.value.currency,
						offer: 'Christmas2022'
					})
				},
				onError: () => {
					console.log('error')
				},
				closeOnSuccess: true
			})

		return {
			t,
			onClose,
			applyDiscount,
			products,
			activeProduct,
			handleSelect,
			handleRestore,
			handleSubmit
		}
	}
}
</script>

<style lang="scss" scoped>
.christmas-offer-paywall {
	background-color: #f5f6f8;

	&__safe-area {
		position: absolute;
	}

	&__header {
		position: relative;
		&__img {
			max-width: 435px;
			width: 100%;
			margin: auto;
			display: block;
		}
		&__title {
			position: absolute;
			top: 70px;
			width: 100%;
		}
	}

	&__subtitle {
		margin-top: 0.2rem;
		margin-bottom: 0.6rem;
	}

	&__list {
		margin-bottom: 1.5rem;

		:deep(.list-item--text) {
			font-size: 1rem;
			line-height: 1.2;
			color: #323653;
		}
	}

	&__switcher {
		margin-bottom: 1.5rem;
	}

	&__terms {
		color: #979aaa;
		font-size: 0.8rem;
		text-align: center;
		letter-spacing: -0.02rem;
		margin-top: 0.8rem;
	}

	&__links {
		margin-top: 0.5rem;
	}

	&__btn {
		width: 100%;
		background: linear-gradient(90.05deg, #52a376 0.03%, #50a373 98.26%);
		border-radius: 100px;
		border: none;
		font-weight: 600;
	}
}
</style>
